import { computed, onBeforeUnmount, ref, watch, type Ref } from 'vue'

/** How far a finger moves before the gesture is read as a drag or a scroll. */
const SLOP = 8
/** A drag past this share of the sheet's height closes it… */
const CLOSE_RATIO = 0.25
/** …and so does a flick faster than this, in px/ms, however short… */
const CLOSE_VELOCITY = 0.5
/** …unless the finger stopped this long before letting go. */
const FLICK_WINDOW = 100

/**
 * Drag a bottom sheet down to close it, which `v-bottom-sheet` does not do
 * (docs/plans/mobile-navigation.md, "Gestures, focus and screen readers"). A finger that goes
 * down from the sheet's top, or from content scrolled to its top, moves the sheet with it; let
 * go past a quarter of its height or with a flick and `onClose` runs, otherwise the sheet
 * springs back. A finger going up, or starting in content scrolled away from its top, scrolls.
 *
 * `sheet` is the element that moves, `scroller` the one inside it that scrolls. Bind `style` on
 * the sheet and call `reset` once it has left, so that it opens at rest next time.
 */
export function useSheetDrag(
  sheet: Ref<HTMLElement | null | undefined>,
  onClose: () => void,
  scroller: () => HTMLElement | null | undefined = () => null,
) {
  const offset = ref(0)
  const dragging = ref(false)

  let mode: 'idle' | 'pending' | 'drag' | 'scroll' = 'idle'
  let startY = 0
  let lastY = 0
  let lastT = 0
  let velocity = 0

  function onStart(event: TouchEvent) {
    const touch = event.touches[0]
    if (event.touches.length !== 1 || !touch) {
      mode = 'idle'
      return
    }
    const content = scroller()
    if (content?.contains(event.target as Node) && content.scrollTop > 0) {
      mode = 'scroll'
      return
    }
    mode = 'pending'
    startY = lastY = touch.clientY
    lastT = event.timeStamp
    velocity = 0
  }

  function onMove(event: TouchEvent) {
    const touch = event.touches[0]
    if (mode === 'idle' || mode === 'scroll' || !touch) return
    const y = touch.clientY
    const dy = y - startY
    if (mode === 'pending') {
      if (Math.abs(dy) < SLOP) return
      mode = dy > 0 ? 'drag' : 'scroll'
      if (mode === 'scroll') return
      dragging.value = true
    }
    // The sheet follows the finger instead of the page or the content scrolling.
    event.preventDefault()
    const dt = event.timeStamp - lastT
    if (dt > 0) velocity = (y - lastY) / dt
    lastY = y
    lastT = event.timeStamp
    offset.value = Math.max(0, dy)
  }

  function onEnd(event: TouchEvent) {
    if (mode === 'drag') {
      const height = sheet.value?.offsetHeight ?? Infinity
      const flicked = velocity > CLOSE_VELOCITY && event.timeStamp - lastT < FLICK_WINDOW
      dragging.value = false
      if (offset.value > height * CLOSE_RATIO || flicked) {
        onClose()
      } else {
        offset.value = 0
      }
    }
    mode = 'idle'
  }

  function bind(el: HTMLElement) {
    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', onEnd)
    el.addEventListener('touchcancel', onEnd)
  }

  function unbind(el: HTMLElement) {
    el.removeEventListener('touchstart', onStart)
    el.removeEventListener('touchmove', onMove)
    el.removeEventListener('touchend', onEnd)
    el.removeEventListener('touchcancel', onEnd)
  }

  // The sheet's content is mounted when it first opens, not with the component.
  watch(sheet, (el, previous) => {
    if (previous) unbind(previous)
    if (el) bind(el)
  })

  onBeforeUnmount(() => {
    if (sheet.value) unbind(sheet.value)
  })

  /** Where the finger holds the sheet; no transition while it does, so it does not lag. */
  const style = computed(() => ({
    transform: offset.value > 0 ? `translateY(${offset.value}px)` : undefined,
    transition: dragging.value ? 'none' : undefined,
  }))

  function reset() {
    offset.value = 0
    dragging.value = false
    mode = 'idle'
  }

  return { style, dragging, reset }
}
